import React from 'react';
import type { CourtIntelligenceStatusResponse } from './templateManagementService';

interface CrawlerStatusBarProps {
  templatesCount: number;
  status: CourtIntelligenceStatusResponse | null;
  loading?: boolean;
  runningManualRun?: boolean;
  generatingTemplate?: boolean;
  targetJurisdiction?: string;
  targetPracticeArea?: string;
  existingTemplateName?: string;
  onRunManual?: () => void;
  onGenerateTemplate?: () => void;
}

function formatRunTimestamp(timestamp: string | null | undefined): string {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString();
}

const CrawlerStatusBar: React.FC<CrawlerStatusBarProps> = ({
  templatesCount,
  status,
  loading = false,
  runningManualRun = false,
  generatingTemplate = false,
  targetJurisdiction,
  targetPracticeArea,
  existingTemplateName,
  onRunManual,
  onGenerateTemplate,
}) => {
  const pipelineActive = status?.pipeline_active ?? false;
  const lastRun = formatRunTimestamp(status?.last_run?.completed_at || status?.last_run?.created_at);
  const pendingReviewCount = status?.pending_review_count ?? 0;
  const hasTarget = Boolean(targetJurisdiction && targetPracticeArea);
  const targetLabel = hasTarget
    ? `${targetJurisdiction} · ${targetPracticeArea}`
    : null;
  const isUpdate = Boolean(existingTemplateName);
  const verb = isUpdate ? 'Update' : 'Generate';

  const generateLabel = generatingTemplate
    ? `${verb === 'Update' ? 'Updating' : 'Generating'}...`
    : targetLabel
      ? `${verb} Template [${targetLabel}]`
      : `${verb} Template`;

  const tooltip = !hasTarget
    ? 'Select a jurisdiction and practice area first'
    : isUpdate
      ? `Update existing system template "${existingTemplateName}" with latest court data`
      : `Create a new system template for ${targetLabel}`;

  return (
    <div className="mt-6 rounded-lg border border-lex-accent bg-lex-accent-soft p-4 dark:border-lex-accent dark:bg-lex-accent-soft">
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-lex-accent dark:text-lex-accent">
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 font-semibold">
            <span className={`h-2.5 w-2.5 rounded-full ${pipelineActive ? 'bg-emerald-500' : 'bg-gray-400'}`} />
            {pipelineActive ? 'Pipeline Active' : 'Pipeline Idle'}
          </span>
          <span className="text-lex-accent dark:text-lex-accent">
            Last run: {loading ? 'Loading...' : lastRun}
          </span>
          <span className="text-lex-accent dark:text-lex-accent">Templates loaded: {templatesCount}</span>
          <span className="text-lex-accent dark:text-lex-accent">
            Review queue: {loading ? 'Loading...' : pendingReviewCount}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onGenerateTemplate}
            disabled={!onGenerateTemplate || generatingTemplate || !hasTarget}
            title={tooltip}
            className="inline-flex items-center rounded-md border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-100 dark:hover:bg-emerald-900/30"
          >
            {generateLabel}
          </button>
          <button
            type="button"
            onClick={onRunManual}
            disabled={!onRunManual || runningManualRun}
            className="inline-flex items-center rounded-md border border-lex-accent bg-white px-3 py-1.5 text-xs font-semibold text-lex-accent hover:bg-lex-state-hover disabled:cursor-not-allowed disabled:opacity-60 dark:border-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent dark:hover:bg-lex-state-hover"
          >
            {runningManualRun ? 'Running...' : 'Run Manual Crawl'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CrawlerStatusBar;
